import React, {
  Fragment,
  useContext,
  useCallback,
  useState,
  useEffect,
} from 'react';
import Store from '../Store/Store';
import Comment from '../Comment/Comment';
import CartSummary from './CartSummary';
import './TargetOrder.css';
import CbmContext from '../../Context/Cbm/cbmContext';
import { useFormSetup } from '../../hooks/useFormSetup';
import ValidationErrors from '../Utils/ValidationErrors';
import { findStoreById } from '../Utils/helperFuncs';

const TargetOrder = ({ setPayLoad, setFormError, errMsg }) => {
  const cbmContext = useContext(CbmContext);
  const {
    stores,
    getTargetItemsPaginated,
    paginatedTargetItems,
    targetItemsTotalPages,
    targetItemsTotalElements,
    targetItemsHasNext,
    targetItemsHasPrevious,
    portalUser,
    auth,
    authLoading,
    submittedData,
    errorCode,
  } = cbmContext;

  const [targetOrderData, setTargetOrderData] = useState({
    store: {
      district: {},
    },
    targetOrderItems: [],
    comments: '',
    portalUser,
  });

  // Use unified form setup hook
  const { validationErrors, getFieldError } = useFormSetup({
    formName: 'targetOrder',
    formData: targetOrderData,
    setFormData: setTargetOrderData,
    setPayLoad,
    setFormError,
    submittedData,
    errorCode,
  });

  // Pagination-specific state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Fetch paginated target items
    if (portalUser && auth && !authLoading) {
      getTargetItemsPaginated({
        page: currentPage,
        size: pageSize,
        sortBy: 'itemType',
        sortDirection: 'asc',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, portalUser, auth, authLoading]);

  const handleTargetItems = useCallback(
    (e, itemName, itemType) => {
      const { value } = e.target;
      setTargetOrderData((prev) => {
        const tempItems = [...prev.targetOrderItems];
        const duplicateIndex = tempItems.findIndex(
          (item) => item.name === itemName
        );

        if (value === '') {
          if (duplicateIndex !== -1) {
            tempItems.splice(duplicateIndex, 1);
          }
        } else if (duplicateIndex === -1) {
          tempItems.push({ name: itemName, quantity: value });
        } else if (itemType !== 'nonRadio') {
          if (value === '0') {
            tempItems.splice(duplicateIndex, 1);
          } else {
            tempItems[duplicateIndex].quantity = value;
          }
        }
        return { ...prev, targetOrderItems: tempItems };
      });
    },
    [setTargetOrderData]
  );

  const handleRemoveItem = useCallback(
    (itemName) => {
      setTargetOrderData((prev) => {
        const tempItems = [...prev.targetOrderItems];
        const deleteIndex = tempItems.findIndex(
          (item) => item.name === itemName
        );
        if (deleteIndex !== -1) {
          tempItems.splice(deleteIndex, 1);
        }
        return { ...prev, targetOrderItems: tempItems };
      });
    },
    [setTargetOrderData]
  );

  const goToPage = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const nextPage = useCallback(() => {
    if (targetItemsHasNext) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [targetItemsHasNext]);

  const previousPage = useCallback(() => {
    if (targetItemsHasPrevious) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [targetItemsHasPrevious]);

  const filteredItems = paginatedTargetItems.filter((item) =>
    item.itemName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const generateItemList = () => {
    let list;
    list = filteredItems.map((item, index) => {
      if (item.itemType === 'nonRadio') {
        return (
          <Fragment key={'item_' + index + 1}>
            <div className="itemCard">
              <div className="itemHeader">
                <p className="itemTitle">{item.itemName}</p>
                <span className="itemBadge custom">Custom Quantity</span>
              </div>
              <div className="itemQuantityInput">
                <label htmlFor={`${item.itemName}_id`}>Enter quantity:</label>
                <input
                  type="number"
                  name="nonRadioItem"
                  id={`${item.itemName}_id`}
                  min="0"
                  placeholder="0"
                  onChange={(e) =>
                    handleTargetItems(e, item.itemName, item.itemType, item.id)
                  }
                />
              </div>
            </div>
          </Fragment>
        );
      } else {
        const isBundled = item.itemType === 'radio6';
        return (
          <Fragment key={'item_' + index + 1}>
            <div className="itemCard">
              <div className="itemHeader">
                <p className="itemTitle">{item.itemName}</p>
                <span
                  className={`itemBadge ${
                    isBundled ? 'bundled' : 'individual'
                  }`}
                >
                  {isBundled ? 'Sold in Bundles' : 'Sold Individually'}
                </span>
              </div>
              <div className="itemQuantityRadio">
                <label>
                  <input
                    type="radio"
                    name={`radioItem_${index}`}
                    id={`${item.itemName}_1_id`}
                    value={item.itemType === 'radio6' ? 1 * 6 : 1}
                    onChange={(e) =>
                      handleTargetItems(
                        e,
                        item.itemName,
                        item.itemType,
                        item.id
                      )
                    }
                  />
                  <span>
                    {item.itemType === 'radio6' ? '1 Bundle (6)' : '1 Unit'}
                  </span>
                </label>
                <label>
                  <input
                    type="radio"
                    name={`radioItem_${index}`}
                    id={`${item.itemName}_2_id`}
                    value={item.itemType === 'radio6' ? 2 * 6 : 2}
                    onChange={(e) =>
                      handleTargetItems(
                        e,
                        item.itemName,
                        item.itemType,
                        item.id
                      )
                    }
                  />
                  <span>
                    {item.itemType === 'radio6' ? '2 Bundles (12)' : '2 Units'}
                  </span>
                </label>
              </div>
            </div>
          </Fragment>
        );
      }
    });
    return list;
  };

  const changeStoreInfo = useCallback(
    (e) => {
      const { value } = e.target;
      const foundStore = findStoreById(stores, parseInt(value));
      setTargetOrderData((prev) => ({ ...prev, store: foundStore }));
    },
    [stores, setTargetOrderData]
  );

  const handleComments = useCallback(
    (e) => {
      const { value } = e.target;
      setTargetOrderData((prev) => ({ ...prev, comments: value }));
    },
    [setTargetOrderData]
  );
  return (
    <Fragment>
      <div className="heading">
        <h1>Target Order</h1>
      </div>
      {errMsg && (
        <div className="error-message-container">
          <p className="error-message">{errMsg}</p>
          <ValidationErrors errors={validationErrors} />
        </div>
      )}
      <fieldset>
        <legend>Store Info</legend>
        <Store
          changeStoreInfo={changeStoreInfo}
          storeValue={targetOrderData.store?.id}
          storeId={'targetOrderLocation'}
          header="Location"
          storeList={stores}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      </fieldset>

      <div className="targetOrderLayout">
        <div className="targetOrderMain">
          <fieldset className="targetOrderInfo">
            <legend>Target Order Items</legend>

            {/* Search Box */}
            <div className="itemsSearchBox">
              <input
                type="text"
                placeholder="Search items by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="itemsSearchInput"
              />
              {searchTerm && (
                <button
                  className="itemsSearchClear"
                  onClick={() => setSearchTerm('')}
                  type="button"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Items Count Info */}
            <div className="itemsInfo">
              <p>
                {searchTerm
                  ? `Showing ${filteredItems.length} of ${targetItemsTotalElements} items`
                  : `Showing ${paginatedTargetItems.length} of ${targetItemsTotalElements} items`}
                {targetItemsTotalPages > 0 && (
                  <span>
                    {' '}
                    (Page {currentPage + 1} of {targetItemsTotalPages})
                  </span>
                )}
              </p>
            </div>

            {/* Items Grid */}
            <div className="targetItems">
              {paginatedTargetItems.length > 0 ? (
                generateItemList()
              ) : (
                <div className="noItems">Loading items...</div>
              )}
            </div>

            {/* Pagination Controls */}
            {!searchTerm && targetItemsTotalPages > 1 && (
              <div className="itemsPagination">
                <button
                  onClick={previousPage}
                  disabled={!targetItemsHasPrevious}
                  className="paginationBtn"
                  type="button"
                >
                  ← Previous
                </button>

                <div className="paginationPages">
                  {Array.from({ length: targetItemsTotalPages }, (_, i) => {
                    // Show first page, last page, current page, and pages around current
                    if (
                      i === 0 ||
                      i === targetItemsTotalPages - 1 ||
                      Math.abs(i - currentPage) <= 1
                    ) {
                      return (
                        <button
                          key={i}
                          onClick={() => goToPage(i)}
                          className={`paginationPageBtn ${
                            i === currentPage ? 'active' : ''
                          }`}
                          type="button"
                        >
                          {i + 1}
                        </button>
                      );
                    } else if (Math.abs(i - currentPage) === 2) {
                      return (
                        <span key={i} className="paginationEllipsis">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={nextPage}
                  disabled={!targetItemsHasNext}
                  className="paginationBtn"
                  type="button"
                >
                  Next →
                </button>
              </div>
            )}
          </fieldset>
        </div>

        <div className="targetOrderSidebar">
          <CartSummary
            cartItems={targetOrderData.targetOrderItems}
            onRemoveItem={handleRemoveItem}
          />
        </div>
      </div>
      <fieldset className="commentInfo">
        <legend>Comments</legend>
        <div className="comments">
          <Comment
            onHandleChange={handleComments}
            payload={targetOrderData.comments}
            getFieldError={getFieldError}
            errMsg={errMsg}
          />
        </div>
      </fieldset>
    </Fragment>
  );
};
// eslint-disable-next-line
TargetOrder.propTypes = {};

export default TargetOrder;
